import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Laptop, ShieldCheck, Trash2, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—');

const DeviceRow = ({ device, onRevoke, busy }) => (
  <div className="flex items-center justify-between gap-4 py-3 border-b last:border-0" data-testid={`trusted-device-${device.device_id}`}>
    <div className="flex items-start gap-3 min-w-0">
      <Laptop className="w-5 h-5 text-slate-500 mt-0.5 shrink-0" />
      <div className="min-w-0">
        <p className="font-medium text-sm flex items-center gap-2">
          {device.device}
          {device.is_current && <Badge className="bg-emerald-600 hover:bg-emerald-600 text-white" data-testid="trusted-device-current-badge">This device</Badge>}
        </p>
        <p className="text-xs text-muted-foreground">
          IP {device.ip_address || 'unknown'} · Added {fmt(device.created_at)} · Expires {fmt(device.expires_at)}
        </p>
      </div>
    </div>
    <Button variant="outline" size="sm" disabled={busy} onClick={() => onRevoke(device)}
      className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`revoke-device-${device.device_id}`}>
      <Trash2 className="w-4 h-4 mr-1" /> Revoke
    </Button>
  </div>
);

const TrustedDevicesCard = () => {
  const [devices, setDevices] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/trusted-devices`, { withCredentials: true });
      setDevices(res.data.devices);
    } catch {
      setDevices([]);
      toast.error('Could not load trusted devices');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const revoke = async (device) => {
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/trusted-devices${device ? `/${device.device_id}` : ''}`, { withCredentials: true });
      toast.success(device ? `${device.device} will need a sign-in code next time` : 'All trusted devices revoked');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Revoke failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="shadow-soft border-border/40 mb-6" data-testid="trusted-devices-card">
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div>
          <CardTitle className="flex items-center gap-2 text-lg"><ShieldCheck className="w-5 h-5 text-emerald-600" /> Trusted devices</CardTitle>
          <CardDescription>Devices that skip the admin sign-in code for 30 days. Revoke any you don't recognise.</CardDescription>
        </div>
        {devices?.length > 0 && (
          <Button variant="destructive" size="sm" disabled={busy} onClick={() => revoke(null)} data-testid="revoke-all-devices-btn">
            Revoke all
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {devices === null && <Loader2 className="w-5 h-5 animate-spin text-slate-400" />}
        {devices?.length === 0 && (
          <p className="text-sm text-muted-foreground" data-testid="trusted-devices-empty">
            No trusted devices. Every admin sign-in will ask for an emailed code.
          </p>
        )}
        {devices?.map((d) => <DeviceRow key={d.device_id} device={d} onRevoke={revoke} busy={busy} />)}
      </CardContent>
    </Card>
  );
};

export default TrustedDevicesCard;
